import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
  Box,
  Stack,
} from "@mui/material";
import api from "../../../../services/api";
import { useSnackbarAlert } from "../../../../hooks/useSnackbarAlert";
import getCostPerBaseUnit from "../../../../utils/cost/getCostPerBaseUnit";

import ProductSelectorSection from "./ProductSelectorSection";
import BatchInfoSection from "./BatchInfoSection";
import IngredientSection from "./IngredientSection";
import ComponentSection from "./ComponentSection";
import LaborAndPackagingSection from "./LaborAndPackagingSection";
import PricingSection from "./PricingSection";
import ComponentMetaSection from "./ComponentMetaSection";

const emptyProfile = {
  productId: "",
  type: "",
  batchSize: 1,
  yieldNotes: "",
  ingredients: [],
  components: [],
  includePackaging: false,
  packaging: [],
  includeLabor: false,
  laborMinutes: 0,
  laborRate: 0,
  wasteBufferPercent: 0,
  manualAdjustments: [],
  unitPrice: 0,
  isComponent: false,
  componentYieldGrams: undefined,
};

const CostProfileFormModal = ({ open, initialData, onClose, onSuccess }) => {
  const isEditing = !!initialData?._id;
  const snackbar = useSnackbarAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [form, setForm] = useState(emptyProfile);
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [componentProfiles, setComponentProfiles] = useState([]);

  useEffect(() => {
    if (initialData) {
      setForm({
        ...emptyProfile,
        ...initialData,
        ingredients: initialData.ingredients || [],
        components: initialData.components || [],
      });
    } else {
      setForm(emptyProfile);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchAllProducts = async () => {
      const [standard, specialty, cheesecakes, patisserie] = await Promise.all([
        api.get("/standard-cakes"),
        api.get("/specialty-cakes"),
        api.get("/cheesecakes"),
        api.get("/patisserie"),
      ]);

      return [
        ...standard.data.map((p) => ({ ...p, type: "standardCake" })),
        ...specialty.data.map((p) => ({ ...p, type: "specialtyCake" })),
        ...cheesecakes.data.map((p) => ({ ...p, type: "cheesecake" })),
        ...patisserie.data.map((p) => ({ ...p, type: "patisserie" })),
      ];
    };

    const fetchData = async () => {
      const [ingredientRes, productData, componentRes] = await Promise.all([
        api.get("/admin/ingredients"),
        fetchAllProducts(),
        api.get("/admin/cost-profiles?isComponent=true"),
      ]);
      setIngredients(ingredientRes.data);
      setProducts(productData);
      setComponentProfiles(componentRes.data);
    };

    fetchData();
  }, []);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        ingredients: (form.ingredients || []).map((i) => ({
          ingredientId: i.ingredientId,
          quantity: Number(i.quantity),
          unit: i.unit,
        })),
        components: (form.components || []).map((c) => ({
          componentId: c.componentId,
          usedGrams: Number(c.usedGrams),
        })),
      };

      if (isEditing) {
        await api.patch(`/admin/cost-profiles/${form._id}`, payload);
        snackbar.success("Profile updated.");
      } else {
        await api.post("/admin/cost-profiles", payload);
        snackbar.success("Profile created.");
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      snackbar.error("Failed to save profile.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper">
      <DialogTitle sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 } }}>
        {isEditing ? "Edit" : "Add"} Cost Profile
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ px: { xs: 2, md: 3 }, py: { xs: 1, md: 2 } }}>
        <Stack spacing={3}>
          <ProductSelectorSection
            form={form}
            updateField={updateField}
            products={products}
          />

          <BatchInfoSection form={form} updateField={updateField} />

          <IngredientSection
            form={form}
            updateField={updateField}
            ingredients={ingredients}
          />

          <ComponentSection
            form={form}
            updateField={updateField}
            componentProfiles={componentProfiles}
          />

          <LaborAndPackagingSection form={form} updateField={updateField} />

          <PricingSection form={form} updateField={updateField} />

          <ComponentMetaSection form={form} updateField={updateField} />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2 },
        }}>
        <Button onClick={onClose} fullWidth={isMobile}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} fullWidth={isMobile}>
          {isEditing ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CostProfileFormModal;
